// components/ui/Button.tsx
import { forwardRef } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Loader2 } from 'lucide-react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'warning' | 'success' | 'outline';
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
    glow?: boolean;
    children?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        className,
        variant = 'primary',
        size = 'md',
        isLoading,
        leftIcon,
        rightIcon,
        children,
        disabled,
        fullWidth,
        glow,
        type = 'button',
        ...props
    }, ref) => {

        const variants: Record<string, string> = {
            primary: 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 focus:ring-emerald-500 shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 active:scale-[0.98]',
            secondary: 'bg-white text-slate-700 border-2 border-slate-200 hover:bg-slate-50 hover:border-slate-300 focus:ring-slate-400 shadow-sm hover:shadow-md dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:border-slate-600',
            danger: 'bg-gradient-to-r from-rose-500 to-rose-600 text-white hover:from-rose-600 hover:to-rose-700 focus:ring-rose-500 shadow-lg shadow-rose-500/25 hover:shadow-xl hover:shadow-rose-500/30 active:scale-[0.98]',
            ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:ring-slate-400 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
            warning: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 focus:ring-amber-400 shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 active:scale-[0.98]',
            success: 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 focus:ring-emerald-400 shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 active:scale-[0.98]',
            outline: 'border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 focus:ring-emerald-400 shadow-sm hover:shadow-md dark:text-emerald-400 dark:hover:bg-emerald-950/40',
        };

        const sizes: Record<string, string> = {
            xs: 'px-2.5 py-1.5 text-xs gap-1.5 rounded-lg',
            sm: 'px-3.5 py-2 text-xs gap-1.5 rounded-lg',
            md: 'px-5 py-2.5 text-sm gap-2 rounded-xl',
            lg: 'px-6 py-3 text-sm gap-2 rounded-xl',
            xl: 'px-8 py-3.5 text-base gap-2.5 rounded-xl',
        };

        const isDisabled = disabled || isLoading;

        return (
            <motion.button
                ref={ref}
                type={type}
                whileHover={isDisabled ? undefined : { scale: 1.02, y: -1 }}
                whileTap={isDisabled ? undefined : { scale: 0.97 }}
                disabled={isDisabled}
                className={twMerge(clsx(
                    'inline-flex items-center justify-center font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed select-none',
                    variants[variant],
                    sizes[size],
                    fullWidth && 'w-full',
                    glow && 'animate-glow',
                    className
                ))}
                {...props}
            >
                {isLoading ? (
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                        <Loader2 size={size === 'xs' || size === 'sm' ? 14 : 18} />
                    </motion.div>
                ) : leftIcon ? (
                    <span className="flex-shrink-0">{leftIcon}</span>
                ) : null}
                {children && <span>{children}</span>}
                {!isLoading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
            </motion.button>
        );
    }
);

Button.displayName = 'Button';
export { Button };
export type { ButtonProps };